/**
 * One turn in the transcript. CLONED from the Console (tc-console
 * src/shell/MessageBubble.tsx): the avatar is ALWAYS the cap for the
 * assistant (the square mark — see AssistantAvatar); anything view-specific
 * arrives as children rather than as another flag on this component.
 */

import type { ReactNode } from 'react'
import { Fade, Paper, Stack, Typography } from '@mui/material'
import MessageContent from './MessageContent'
import { StaticCapAvatar } from './AssistantAvatar'
import type { ShowInSurfaceArgs, SurfaceOpenTarget } from './types'

export const CAP_AVATAR = <StaticCapAvatar />

export default function MessageBubble({
  role,
  content,
  userAvatar,
  onOpenSurface,
  onShowInSurface,
  children,
}: {
  role: 'user' | 'assistant'
  content: string
  /** The person's own photo/initials, when the LMS gave us one. */
  userAvatar?: ReactNode
  onOpenSurface?: (id: string, label?: string, target?: SurfaceOpenTarget) => void
  onShowInSurface?: (args: ShowInSurfaceArgs) => void
  children?: ReactNode
}) {
  const mine = role === 'user'
  return (
    <Fade in timeout={350}>
      <Stack
        direction={mine ? 'row-reverse' : 'row'}
        spacing={1} alignItems="flex-start"
        sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '90%' }}
      >
        {mine ? userAvatar : CAP_AVATAR}
        <Paper
          variant={mine ? 'elevation' : 'outlined'}
          elevation={0}
          sx={{
            p: 1.5, borderRadius: 3,
            bgcolor: mine ? 'primary.main' : 'background.paper',
            color: mine ? 'primary.contrastText' : 'text.primary',
            // Assistant replies are Markdown; only user text needs raw newlines.
            whiteSpace: mine ? 'pre-wrap' : 'normal',
            position: 'relative',
            overflowWrap: 'anywhere', wordBreak: 'break-word',
          }}
        >
          {mine ? (
            <Typography variant="body2" component="div">{content}</Typography>
          ) : (
            <MessageContent
              content={content}
              onOpenSurface={onOpenSurface ?? (() => {})}
              onShowInSurface={onShowInSurface ?? (() => {})}
            />
          )}
          {children}
        </Paper>
      </Stack>
    </Fade>
  )
}
